/**
 * pdp-species-swap.js
 * Kineo Mountain Woodsmithing — PDP Species Swap & Sticky Bar
 *
 * Responsibilities:
 *  1. Listen for variant changes and swap the center studio image
 *  2. Manage swatch selected state (ring + opacity)
 *  3. Sync the mobile sticky bar (trigger, panel, prices) with the selected variant
 *  4. Keep all price displays in sync across the page
 *  5. Multi-axis: pills + species swatches resolve to a variant by combination,
 *     across desktop, mobile, AND the sticky pull-up bar.
 */

(function () {
  'use strict';

  // ─── Utility ────────────────────────────────────────────────────────────────

  function formatMoney(cents, moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10);
    const value = (cents / 100).toFixed(2);
    return moneyFormat
      ? moneyFormat.replace('{{amount}}', value).replace('{{amount_no_decimals}}', Math.round(cents / 100))
      : '$' + value;
  }

  // ─── PDP Species Swap Controller ────────────────────────────────────────────

  class PDPSpeciesSwap {
    constructor(container) {
      this.container = container;
      this.productHandle = container.dataset.productHandle;
      this.productData = null;
      this.currentVariant = null;
      this.moneyFormat = window.Shopify?.moneyFormat || '${{amount}}';

      // DOM refs — hero
      this.centerImage = container.querySelector('[data-pdp-center-image]');
      this.swatches = container.querySelectorAll('[data-pdp-swatch]:not([data-option-index])');
      this.priceEls = container.querySelectorAll('[data-pdp-price]');

      // DOM refs — sticky bar
      this.stickyBar = document.querySelector('[data-pdp-sticky-bar]');
      this.stickyATC = document.querySelector('[data-pdp-sticky-atc]');

      this._loadProductData();
      this._initMultiAxis();
      this._bindSwatches();
      this._bindPillsOnly();
      this._bindVariantChangeEvent();
      this._initStickyBar();
      this._initEngraving();
      this._initMobilePersonalize();

      // First paint — runs after multi-axis setup so studio shots filter on load.
      this._syncAll();
    }

    // ── Load product JSON ──────────────────────────────────────────────────────

    _loadProductData() {
      const jsonEl = this.container.querySelector('[data-pdp-product-json]');
      if (jsonEl) {
        try {
          this.productData = JSON.parse(jsonEl.textContent);
          this.currentVariant = this.productData.selected_or_first_available_variant
            || this.productData.variants[0];
          // NOTE: initial _syncAll() is deferred to the constructor, AFTER
          // _initMultiAxis() has set up isMultiAxis + _filterShots, so the first
          // paint filters the studio shots correctly.
        } catch (e) {
          console.error('[PDPSpeciesSwap] JSON PARSE FAILED:', e);
          console.error('[PDPSpeciesSwap] Raw text was:', jsonEl.textContent);
        }
      }
    }

    // ── Multi-axis (species swatch + text pills) ───────────────────────────────

    _initMultiAxis() {
      // Multi-axis is active if either the hero wrap OR the sticky bar carries the flag.
      const wrap = this.container.querySelector('[data-pdp-multi-axis]');
      const stickyWrap = this.stickyBar && this.stickyBar.matches('[data-pdp-sticky-multi]')
        ? this.stickyBar : null;
      this.isMultiAxis = !!(wrap || stickyWrap);
      if (!this.isMultiAxis) return;

      const source = wrap || stickyWrap;
      this.speciesIdx = parseInt(source.dataset.speciesIndex, 10);
      this.extraIdx = parseInt(source.dataset.extraIndex, 10);

      const variantOpts = (v) => v.options || (v.title ? v.title.split(' / ') : []);
      this._variantOpts = variantOpts;

      // Seed selection from current variant
      this.selectedOptions = variantOpts(this.currentVariant).slice();

      // Resolve the selected option combination to a concrete variant
      this._resolveVariant = () => {
        const match = this.productData.variants.find(v => {
          const opts = variantOpts(v);
          return this.selectedOptions.every((sel, i) => opts[i] === sel);
        });
        if (match) this._selectVariant(match);
      };

      // Update EVERY swatch/option price (desktop + mobile + sticky) for the current
      // non-species selection.
      this._updateSwatchPrices = () => {
        const selector = '[data-pdp-swatch-price], [data-pdp-sticky-option-price]';
        this.container.querySelectorAll(selector).forEach(el => this._priceFor(el, variantOpts));
        if (this.stickyBar) {
          this.stickyBar.querySelectorAll(selector).forEach(el => this._priceFor(el, variantOpts));
        }
      };

      // Hide studio shots whose extra-axis value != current selection
      this._filterShots = () => {
        if (this.extraIdx < 0 || isNaN(this.extraIdx)) return;
        const sel = this.selectedOptions[this.extraIdx];
        this.container.querySelectorAll('[data-pdp-shot]').forEach(shot => {
          shot.hidden = shot.dataset.extraValue !== sel;
        });
      };

      // Bind ALL pills (desktop + mobile + sticky) in one pass
      this._allPills().forEach(pill => {
        pill.addEventListener('click', () => {
          this._onPill(parseInt(pill.dataset.optionIndex, 10), pill.dataset.optionValue);
        });
      });

      // Bind ALL multi-axis swatches/options (desktop + mobile + sticky) in one pass
      this._allSpeciesControls().forEach(ctrl => {
        ctrl.addEventListener('click', () => {
          this._onSpecies(
            parseInt(ctrl.dataset.optionIndex, 10),
            ctrl.dataset.optionValue,
            ctrl.dataset.speciesDescription
          );
        });
      });

      // Initial paint
      this._updateSwatchPrices();
      this._filterShots();
    }

    // Price-for-one-element helper (multi-axis)
    _priceFor(priceEl, variantOpts) {
      const speciesVal = priceEl.dataset.speciesValue;
      const match = this.productData.variants.find(v => {
        const opts = variantOpts(v);
        if (opts[this.speciesIdx] !== speciesVal) return false;
        return this.selectedOptions.every((sel, i) => i === this.speciesIdx || opts[i] === sel);
      });
      if (match) priceEl.textContent = formatMoney(match.price, this.moneyFormat);
    }

    _allPills() {
      const hero = Array.from(this.container.querySelectorAll('[data-pdp-pill]'));
      const sticky = this.stickyBar
        ? Array.from(this.stickyBar.querySelectorAll('[data-pdp-pill]')) : [];
      return hero.concat(sticky);
    }

    _allSpeciesControls() {
      // Multi-axis swatches (hero) + sticky options that carry an option-index
      const hero = Array.from(this.container.querySelectorAll('[data-pdp-swatch][data-option-index]'));
      const sticky = this.stickyBar
        ? Array.from(this.stickyBar.querySelectorAll('[data-pdp-sticky-option][data-option-index]')) : [];
      return hero.concat(sticky);
    }

    _onPill(idx, value) {
      this.selectedOptions[idx] = value;
      this._syncPillState(idx, value);
      this._resolveVariant();
      this._updateSwatchPrices();
      this._filterShots();
    }

    _onSpecies(idx, value, desc) {
      this.selectedOptions[idx] = value;
      this._syncSpeciesControlState(idx, value);
      const descEl = this.container.querySelector('[data-pdp-species-description]');
      if (descEl) descEl.textContent = desc || '';
      this._resolveVariant();
    }

    // Mark pills selected across desktop + mobile + sticky, by option value
    _syncPillState(idx, value) {
      this._allPills().forEach(p => {
        if (parseInt(p.dataset.optionIndex, 10) !== idx) return;
        const s = p.dataset.optionValue === value;
        p.classList.toggle('is-selected', s);
        p.setAttribute('aria-checked', s ? 'true' : 'false');
      });
    }

    // Mark species controls selected across desktop + mobile + sticky, by option value
    _syncSpeciesControlState(idx, value) {
      this._allSpeciesControls().forEach(s => {
        if (parseInt(s.dataset.optionIndex, 10) !== idx) return;
        const sel = s.dataset.optionValue === value;
        s.classList.toggle('is-selected', sel);
        if (s.hasAttribute('role')) s.setAttribute('aria-checked', sel ? 'true' : 'false');
        // Sticky option checkmark
        if (s.matches('[data-pdp-sticky-option]')) this._setStickyCheck(s, sel);
      });
    }

    // ── Bind single-axis swatches (variant-id based) ───────────────────────────

    _bindSwatches() {
      if (this.isMultiAxis) return; // multi-axis swatches handled in _initMultiAxis
      this.swatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          const variantId = parseInt(swatch.dataset.variantId, 10);
          const variant = this.productData?.variants.find(v => v.id === variantId);
          if (!variant) return;
          this.swatches.forEach(s => {
            const sel = parseInt(s.dataset.variantId, 10) === variantId;
            s.classList.toggle('is-selected', sel);
            s.setAttribute('aria-checked', sel ? 'true' : 'false');
          });
          this._selectVariant(variant);
        });
      });
    }

    // ── Pills-only products (no species axis, e.g. Cross/Flag) ─────────────────
    // Each priced pill carries data-variant-id, so selection is a direct id lookup.
    // Pills exist in desktop, mobile, AND the sticky bar; bind all and keep state in sync.

    _bindPillsOnly() {
      const desktop = this.container.querySelector('[data-pdp-pills-only]');
      const mobile = this.container.querySelector('[data-pdp-mobile-pills-only]');
      const sticky = this.stickyBar && this.stickyBar.querySelector('[data-pdp-sticky-pills-only]');
      if (!desktop && !mobile && !sticky) return; // not a pills-only product
      this.isPillsOnly = true;

      const allPills = [
        ...this.container.querySelectorAll('[data-pdp-pill][data-variant-id]'),
        ...(this.stickyBar ? this.stickyBar.querySelectorAll('[data-pdp-pill][data-variant-id]') : []),
      ];

      const syncPillState = (value) => {
        allPills.forEach(p => {
          const sel = p.dataset.optionValue === value;
          p.classList.toggle('is-selected', sel);
          p.setAttribute('aria-checked', sel ? 'true' : 'false');
        });
      };

      allPills.forEach(pill => {
        pill.addEventListener('click', () => {
          const variantId = parseInt(pill.dataset.variantId, 10);
          const variant = this.productData?.variants.find(v => v.id === variantId);
          if (!variant) return;
          syncPillState(pill.dataset.optionValue);
          this._selectVariant(variant);
        });
      });
    }

    // ── Listen for theme-native variant change events ──────────────────────────

    _bindVariantChangeEvent() {
      document.addEventListener('product-form:variant-changed', (e) => {
        if (!this.productData) return;
        const variantId = e.detail?.variantId || e.detail?.variant?.id;
        if (!variantId) return;
        const variant = this.productData.variants.find(v => v.id === parseInt(variantId, 10));
        if (variant) this._onVariantChange(variant);
      });

      const variantInput = this.container.querySelector('input[name="id"]');
      if (variantInput) {
        const observer = new MutationObserver(() => {
          const variantId = parseInt(variantInput.value, 10);
          const variant = this.productData?.variants.find(v => v.id === variantId);
          if (variant && variant.id !== this.currentVariant?.id) {
            this._onVariantChange(variant);
          }
        });
        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });

        variantInput.addEventListener('change', () => {
          const variantId = parseInt(variantInput.value, 10);
          const variant = this.productData?.variants.find(v => v.id === variantId);
          if (variant) this._onVariantChange(variant);
        });
      }
    }

    // ── Select a variant (from swatch or sticky bar) ───────────────────────────

    _selectVariant(variant) {
      this.currentVariant = variant;
      const variantInput = this.container.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this._syncAll();
    }

    // ── Called when variant changes from any source ────────────────────────────

    _onVariantChange(variant) {
      this.currentVariant = variant;
      // Keep multi-axis selection model aligned with externally-driven changes
      if (this.isMultiAxis && this._variantOpts) {
        this.selectedOptions = this._variantOpts(variant).slice();
      }
      this._syncAll();
    }

    // ── Sync everything to currentVariant ─────────────────────────────────────

    _syncAll() {
      if (!this.currentVariant) return;
      this._syncCenterImage();
      this._syncSwatchStates();
      this._syncPrices();
      this._syncStickyBar();
      this._syncSpeciesDescription();
      // Re-filter studio shots on EVERY variant change, not just pill clicks.
      // _filterShots is only defined for multi-axis products.
      if (this.isMultiAxis && this._filterShots) this._filterShots();
    }

    // ── Swap center studio image ───────────────────────────────────────────────

    _syncCenterImage() {
      if (!this.centerImage || !this.currentVariant) return;
      const src = this.currentVariant.image;
      if (!src) return;
      const baseSrc = src.split('?')[0];

      this.centerImage.style.opacity = '0';
      this.centerImage.style.transition = 'opacity 0.3s ease';

      setTimeout(() => {
        this.centerImage.src = this._shopifyImageUrl(baseSrc, '800x');
        this.centerImage.srcset = [
          `${this._shopifyImageUrl(baseSrc, '400x')} 400w`,
          `${this._shopifyImageUrl(baseSrc, '800x')} 800w`,
          `${this._shopifyImageUrl(baseSrc, '1200x')} 1200w`,
        ].join(', ');
        this.centerImage.onload = () => { this.centerImage.style.opacity = '1'; };
        if (this.centerImage.complete) this.centerImage.style.opacity = '1';
      }, 150);
    }

    // ── Update swatch selected states (single-axis only) ───────────────────────

    _syncSwatchStates() {
      if (this.isMultiAxis) return; // multi-axis manages its own swatch/pill state
      this.swatches.forEach(swatch => {
        const isSelected = parseInt(swatch.dataset.variantId, 10) === this.currentVariant?.id;
        swatch.classList.toggle('is-selected', isSelected);
        swatch.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
    }

    // ── Sync all price displays ────────────────────────────────────────────────

    _syncPrices() {
      if (!this.currentVariant) return;
      const price = formatMoney(this.currentVariant.price, this.moneyFormat);
      this.priceEls.forEach(el => { el.textContent = price; });
    }

    // ── Sync sticky bar ───────────────────────────────────────────────────────

    _syncStickyBar() {
      if (!this.stickyBar || !this.currentVariant) return;

      // Trigger name + price. For single-variant (one-of-a-kind) products, the
      // Liquid already rendered the product title here; don't overwrite it with
      // the variant's "Default Title".
      const triggerName = this.stickyBar.querySelector('[data-pdp-sticky-name]');
      const triggerPrice = this.stickyBar.querySelector('[data-pdp-sticky-price]');
      const isSingleVariant = (this.productData?.variants?.length || 0) <= 1;
      if (triggerName && !isSingleVariant) triggerName.textContent = this.currentVariant.title;
      if (triggerPrice) triggerPrice.textContent = formatMoney(this.currentVariant.price, this.moneyFormat);

      // Mobile topbar price
      document.querySelectorAll('.pdp-hero__mobile-topbar-price').forEach(el => {
        el.textContent = formatMoney(this.currentVariant.price, this.moneyFormat);
      });

      // Panel option selected states. Single-axis keys off variant id; multi-axis
      // keys off the selected species value and is handled by _syncSpeciesControlState.
      if (!this.isMultiAxis) {
        this.stickyBar.querySelectorAll('[data-pdp-sticky-option]').forEach(option => {
          const isSelected = parseInt(option.dataset.variantId, 10) === this.currentVariant.id;
          option.classList.toggle('is-selected', isSelected);
          this._setStickyCheck(option, isSelected);
        });
      } else {
        // Re-assert multi-axis selected state on the sticky options + pills
        const speciesVal = this.selectedOptions[this.speciesIdx];
        const extraVal = this.selectedOptions[this.extraIdx];
        if (speciesVal !== undefined) this._syncSpeciesControlState(this.speciesIdx, speciesVal);
        if (extraVal !== undefined) this._syncPillState(this.extraIdx, extraVal);
      }

      // ATC
      if (this.stickyATC) {
        this.stickyATC.disabled = !this.currentVariant.available;
        this.stickyATC.textContent = this.currentVariant.available ? 'Add to Cart' : 'Sold Out';
      }
    }

    // Add/remove the checkmark on a sticky option row
    _setStickyCheck(option, isSelected) {
      const existing = option.querySelector('.pdp-sticky-bar__option-check');
      if (isSelected && !existing) {
        const check = document.createElement('span');
        check.className = 'pdp-sticky-bar__option-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        option.appendChild(check);
      } else if (!isSelected && existing) {
        existing.remove();
      }
    }

    _syncSpeciesDescription() {
      const descEl = this.container.querySelector('[data-pdp-species-description]');
      if (!descEl) return;
      const activeSwatch = this.container.querySelector('[data-pdp-swatch].is-selected');
      if (!activeSwatch) return;
      const desc = activeSwatch.dataset.speciesDescription || '';
      descEl.textContent = desc;
    }

    // ── Sticky bar scroll behavior + interactions ──────────────────────────────

    _initStickyBar() {
      if (!this.stickyBar) return;

      // Reveal sticky bar once a sentinel just past the top bar scrolls off.
      const atcButton = this.container.querySelector('.pdp-hero__mobile-topbar-atc')
        || this.container.querySelector('[data-pdp-main-atc]');
      if (!atcButton) {
        this.stickyBar.classList.add('is-visible');
      } else {
        const REVEAL_OFFSET = -105;
        const sentinel = document.createElement('div');
        sentinel.setAttribute('data-pdp-sticky-sentinel', '');
        sentinel.style.cssText = 'position:absolute;width:1px;height:1px;pointer-events:none;';
        const topbar = this.container.querySelector('.pdp-hero__mobile-topbar') || atcButton;
        topbar.style.position = topbar.style.position || 'relative';
        topbar.appendChild(sentinel);
        sentinel.style.bottom = (-1 * REVEAL_OFFSET) + 'px';
        sentinel.style.left = '0';

        const observer = new IntersectionObserver(
          ([entry]) => {
            this.stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
          },
          { threshold: 0 }
        );
        observer.observe(sentinel);
      }

      // Species panel toggle
      const trigger = this.stickyBar.querySelector('[data-pdp-sticky-trigger]');
      const panel = this.stickyBar.querySelector('[data-pdp-sticky-panel]');
      if (trigger && panel) {
        trigger.addEventListener('click', () => {
          const isOpen = panel.classList.toggle('is-open');
          trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        });
      }

      const closePanel = () => {
        if (panel) { panel.classList.remove('is-open'); panel.setAttribute('aria-hidden', 'true'); }
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      };

      // Sticky panel SINGLE-AXIS option selection (variant-id based).
      // Multi-axis sticky options are bound in _initMultiAxis via _allSpeciesControls();
      // here we only close the panel after a multi-axis species pick.
      this.stickyBar.querySelectorAll('[data-pdp-sticky-option]').forEach(option => {
        option.addEventListener('click', () => {
          if (this.isMultiAxis && option.hasAttribute('data-option-index')) {
            closePanel();
            return;
          }
          const variantId = parseInt(option.dataset.variantId, 10);
          const variant = this.productData?.variants.find(v => v.id === variantId);
          if (!variant) return;
          this._selectVariant(variant);
          closePanel();
        });
      });

      // Sticky ATC submit
      if (this.stickyATC) {
        this.stickyATC.addEventListener('click', () => {
          const form = document.getElementById('pdp-hero-form');
          if (!form) return;
          const input = form.querySelector('[name="id"]');
          if (input && this.currentVariant) input.value = this.currentVariant.id;
          form.submit();
        });
      }

      this._initLightbox();
      this._initEngravingSync();
    }

    // ── Move Easify's "Personalize it" trigger into the mobile layout ──────────
    // Easify renders ONE trigger, anchored to the desktop ATC (#AddToCart), which
    // lives inside the hidden .pdp-hero__desktop tree on mobile. We relocate that
    // node into a mobile mount point between the lifestyle image and trust badges.
    // Moving (not cloning) preserves Easify's own click/popup wiring.
    _initMobilePersonalize() {
      const mount = this.container.querySelector('[data-pdp-mobile-personalize-mount]');
      if (!mount) return;

      const breakpoint = 864; // keep in sync with section.settings.mobile_breakpoint
      let homeParent = null;  // where the trigger lived originally (desktop form)
      let homeNext = null;    // its original next sibling, to restore position

      const findTrigger = () =>
        document.querySelector('.tpo-show-popup, .tpo_open-modal-button');

      const place = () => {
        const trigger = findTrigger();
        if (!trigger) return; // Easify may not have injected yet; observer retries
        const isMobile = window.matchMedia(`(max-width: ${breakpoint}px)`).matches;

        if (isMobile) {
          if (trigger.parentNode !== mount) {
            if (!homeParent) {
              homeParent = trigger.parentNode;
              homeNext = trigger.nextSibling;
            }
            mount.appendChild(trigger);
          }
        } else {
          if (homeParent && trigger.parentNode === mount) {
            if (homeNext && homeNext.parentNode === homeParent) {
              homeParent.insertBefore(trigger, homeNext);
            } else {
              homeParent.appendChild(trigger);
            }
          }
        }
      };

      // Easify injects asynchronously; watch for the trigger appearing, then place.
      const observer = new MutationObserver(() => {
        if (findTrigger()) place();
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Place on load, on viewport changes, and via a few delayed retries.
      place();
      window.matchMedia(`(max-width: ${breakpoint}px)`).addEventListener('change', place);
      [300, 800, 1500].forEach(ms => setTimeout(place, ms));
    }

    // ── Engraving toggle + char counter ────────────────────────────────────────

    _initEngraving() {
      const wrap = this.container.querySelector('[data-pdp-engraving]');
      if (!wrap) return;
      const toggle = wrap.querySelector('[data-pdp-engraving-toggle]');
      const field = wrap.querySelector('[data-pdp-engraving-field]');
      const input = wrap.querySelector('[data-pdp-engraving-input]');
      const count = wrap.querySelector('[data-pdp-engraving-count]');
      if (!toggle || !field || !input) return;

      toggle.addEventListener('change', () => {
        if (toggle.checked) {
          field.hidden = false;
          input.disabled = false;
          input.focus();
        } else {
          field.hidden = true;
          input.disabled = true;
          input.value = '';
          if (count) count.textContent = '0/15';
        }
      });

      input.addEventListener('input', () => {
        if (count) count.textContent = `${input.value.length}/15`;
      });
    }

    // ── Lightbox ───────────────────────────────────────────────────────────────

    _initLightbox() {
      const lightbox = document.querySelector('[data-pdp-lightbox]');
      const lightboxImg = document.querySelector('[data-pdp-lightbox-img]');
      const lightboxClose = document.querySelector('[data-pdp-lightbox-close]');
      if (!lightbox || !lightboxImg) return;

      document.querySelectorAll('[data-lightbox-src]').forEach(btn => {
        btn.addEventListener('click', () => {
          lightboxImg.src = btn.dataset.lightboxSrc;
          lightboxImg.alt = btn.dataset.lightboxAlt || '';
          lightbox.classList.add('is-open');
          lightbox.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        });
      });

      const closeLightbox = () => {
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        lightboxImg.src = '';
      };

      if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
      lightbox.addEventListener('click', e => { if (e.target === lightbox) closeLightbox(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') closeLightbox(); });
    }

    // ── Engraving checkbox sync (mobile inline ↔ sticky panel ↔ form) ──────────

    _initEngravingSync() {
      const mobileCheck = document.getElementById('pdp-mobile-engraving-checkbox');
      const stickyCheck = document.getElementById('pdp-sticky-engraving-checkbox');
      const mobileInput = document.getElementById('pdp-mobile-engraving-input');
      const stickyInput = document.getElementById('pdp-sticky-engraving-input');
      const formInput = this.container.querySelector('[data-pdp-engraving-input]');
      if (!mobileCheck && !stickyCheck) return;

      const syncChecked = (source) => {
        const checked = source.checked;
        [mobileCheck, stickyCheck].forEach(el => {
          if (el && el !== source) el.checked = checked;
        });
        document.querySelectorAll('[data-pdp-engraving-field]').forEach(field => {
          field.hidden = !checked;
          const input = field.querySelector('[data-pdp-engraving-input]');
          if (input) input.disabled = !checked;
        });
      };

      const syncText = (source) => {
        const val = source.value;
        [mobileInput, stickyInput, formInput].forEach(el => {
          if (el && el !== source) el.value = val;
        });
      };

      if (mobileCheck) mobileCheck.addEventListener('change', () => syncChecked(mobileCheck));
      if (stickyCheck) stickyCheck.addEventListener('change', () => syncChecked(stickyCheck));
      if (mobileInput) mobileInput.addEventListener('input', () => syncText(mobileInput));
      if (stickyInput) stickyInput.addEventListener('input', () => syncText(stickyInput));
    }

    // ── Shopify image URL helper ───────────────────────────────────────────────

    _shopifyImageUrl(src, size) {
      return src.replace(/(\.[^.]+)$/, `_${size}$1`);
    }
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    const container = document.querySelector('[data-pdp-species-swap]');
    if (container) new PDPSpeciesSwap(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
